import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, MessageSquareText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { projectId } from '../utils/supabase/info';
import type { StatusNote } from '../utils/clientHome';

/**
 * Consultant editor for the status note the client sees under "Where we are" on the client home.
 * Reads and writes /client/status-note/:companyId (clientHomeRoutes.ts; writing is firm-only).
 */

interface ClientStatusNoteCardProps {
  companyId: string;
  accessToken: string;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export function ClientStatusNoteCard({ companyId, accessToken }: ClientStatusNoteCardProps) {
  const [note, setNote] = useState<StatusNote | null>(null);
  const [text, setText] = useState('');
  const [next, setNext] = useState('');
  const [then, setThen] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${BASE}/client/status-note/${encodeURIComponent(companyId)}`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const n: StatusNote | null = body?.statusNote ?? null;
        setNote(n);
        setText(n?.text ?? '');
        setNext(n?.next ?? '');
        setThen(n?.then ?? '');
      })
      .catch(() => { /* empty editor */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, accessToken]);

  const dirty = text !== (note?.text ?? '') || next !== (note?.next ?? '') || then !== (note?.then ?? '');

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/client/status-note/${encodeURIComponent(companyId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, next, then }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      setNote(body.statusNote ?? null);
      toast.success('Status update saved. The client sees it on their home page.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not save the status update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="client-status-note-card">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-semibold flex items-center gap-2">
              <MessageSquareText className="size-4 text-indigo-600" />
              Status update for the client
            </h3>
            <p className="text-sm text-gray-500 mt-1">Shown under "Where we are" on the client's home page. Plain language, a few sentences.</p>
          </div>
          {note?.updatedAt && (
            <span className="text-xs text-gray-500">Last updated by {note.authorName || 'a consultant'} on {new Date(note.updatedAt).toLocaleDateString()}</span>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="size-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="status-note-text">Update</Label>
              <Textarea id="status-note-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="We are on track. Once the remaining questions are in, we will draft the design and walk you through it." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="status-note-next">Next</Label>
                <Input id="status-note-next" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Workshop, Nov 3, 10:00" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="status-note-then">Then</Label>
                <Input id="status-note-then" value={then} onChange={(e) => setThen(e.target.value)} placeholder="Design review, mid-November" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving || !dirty}>
                {saving && <Loader2 className="size-4 animate-spin mr-2" />}
                Save update
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
